// © Microsoft Corporation. All rights reserved.

import { AadData, AccountInfo, clientId, getAppEnvironment, oneServiceScope } from "@msnews/core";

import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { ExperiencesSharedStateConnector } from "@msnews/experiences-shared-state";
import { PageBase } from "@msnews/experiences-redux";

/**
 * chrome
 * The chrome API is not defined in Typescript
 */
declare global {
    // tslint:disable-next-line: completed-docs
    interface Window { chrome: any; }
}

/** The aadToken QS key */
export const aadTokenQsKey = "stn";

/** The aadToken QS value */
export const aadTokenQsVal = "aad";

/**
 * Gets a value to indicate if we are in an enterprise scenario
 * eg. whether we should append the AAD Token as part of the call to oneservice endpoints
 * The default value is false, so we don't need to wait until the value is explicitly set
 */
export function useEnterpriseCompliance(): boolean {
    // Obtain the current value of useEnterpriseCompliance from the ExperiencesSharedState connector
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    return sharedStateConnector ? sharedStateConnector.getCurrentState().useEnterpriseCompliance : false;
}

/**
 * Checks if aad token is invalid for user
 */
export function isInvalidAadToken(aadData: AadData): boolean {
    return !aadData || !aadData.isSuccess || !aadData.isValid;
}

export async function getAadData(): Promise<AadData> {
    const accountInfo = await resolveAccountInfo();

    if (!accountInfo) {
        return null;
    }

    return new Promise((resolve) => {
        const tokenAcquireParameters = {
            account_id: accountInfo.account_id,
            account_type: accountInfo.account_type,
            client_id: clientId,
            scope_or_resource: oneServiceScope
        };

        window.chrome.authPrivate.acquireAccessTokenSilently(tokenAcquireParameters, (acquireResult) => {
            resolve({
                token: acquireResult.access_token,
                errorInfo: acquireResult.error_info,
                isSuccess: acquireResult.is_success,
                isValid: acquireResult.is_token_valid
            });
        });
    });
}

/**
 * Resolve account info promise
 */
export async function resolveAccountInfo(): Promise<AccountInfo> {
    const appEnvironment = getAppEnvironment();

    if (!appEnvironment.AccountInfoPromise) {
        return null;
    }

    return await appEnvironment.AccountInfoPromise;
}

/**
 * Gets a value to indicate if we should use enterprise domain
 * eg. whether we should use the Falcon domain vs. consumer domain for oneservice requests
 * The default value is false, so we don't need to wait until the value is explicitly set
 */
export function useEnterpriseDomain(): boolean {
    // Obtain the current value of useEnterpriseDomain from the ExperiencesSharedState connector
    const sharedStateConnector: ExperiencesSharedStateConnector = PageBase.getInstance().rootReducer.connector(ConnectorNamespaces.SharedState) as ExperiencesSharedStateConnector;
    return sharedStateConnector ? sharedStateConnector.getCurrentState().useEnterpriseDomain : false;
}